import { useState, useEffect } from 'react'
import { Check, Sparkles } from 'lucide-react'
import ResumePreview from './ResumePreview'

interface Template {
  id: string
  name: string
  description: string
  category: string
  color: string
  preview: string
  features: string[]
}

interface TemplateGalleryProps {
  selectedTemplate: string
  onSelect: (templateId: string) => void
  onClose: () => void
}

const templates: Template[] = [
  {
    id: 'modern',
    name: 'Modern Professional',
    description: 'Clean and contemporary design with blue accents and timeline layout',
    category: 'Professional',
    color: 'from-blue-500 to-blue-700',
    preview: 'Modern template with header gradient, timeline experience section, and professional layout',
    features: ['Timeline Design', 'Blue Gradient Header', 'Skills Grid', 'Icons']
  },
  {
    id: 'functional',
    name: 'Functional Skills',
    description: 'Skills-first approach with teal gradient header and competency grid',
    category: 'Functional',
    color: 'from-teal-500 to-cyan-600',
    preview: 'Functional template highlighting core competencies with teal accents and skill categories',
    features: ['Skills First', 'Competency Grid', 'Teal Accents', 'Category Boxes']
  },
  {
    id: 'combined',
    name: 'Combined Dual-Tone',
    description: 'Two-column design with orange sidebar and gray header',
    category: 'Modern',
    color: 'from-orange-500 via-orange-600 to-gray-800',
    preview: 'Combined layout with orange sidebar for skills and main content area',
    features: ['Dual Column', 'Orange Sidebar', 'Gray Header', 'Modern Layout']
  },
  {
    id: 'hybrid',
    name: 'Hybrid Purple',
    description: 'Professional purple design with vertical accent bars and centered header',
    category: 'Professional',
    color: 'from-purple-600 via-violet-600 to-purple-700',
    preview: 'Hybrid template with purple accents, vertical bars, and rounded skill pills',
    features: ['Purple Accents', 'Vertical Bars', 'Centered Header', 'Skill Pills']
  },
  {
    id: 'executive',
    name: 'Executive Professional',
    description: 'Sophisticated corporate design with elegant typography and structure',
    category: 'Executive',
    color: 'from-gray-800 to-gray-900',
    preview: 'Executive template with bold header border, two-column layout, and refined styling',
    features: ['Bold Top Border', 'Two-Column Layout', 'Elegant Typography', 'Corporate']
  },
  {
    id: 'technical',
    name: 'Technical Developer',
    description: 'Code-inspired design perfect for software engineers and developers',
    category: 'Technical',
    color: 'from-green-600 to-gray-900',
    preview: 'Tech template with terminal-style header, code comments, and prominent skills grid',
    features: ['Terminal Style', 'Code Comments', 'Skills Grid', 'Monospace Font']
  },
  {
    id: 'aspire',
    name: 'Aspire Dynamic',
    description: 'Bold and ambitious design with vibrant gradients and angular elements',
    category: 'Creative',
    color: 'from-indigo-600 via-purple-600 to-pink-500',
    preview: 'Dynamic template with angled header, gradient accents, and colorful skill tags',
    features: ['Angled Header', 'Gradient Accents', 'Colorful Tags', 'Modern']
  },
  {
    id: 'traditional',
    name: 'Classic Traditional',
    description: 'Timeless design perfect for corporate and formal positions',
    category: 'Classic',
    color: 'from-gray-600 to-gray-800',
    preview: 'Traditional resume with clean sections, professional typography, and structured layout',
    features: ['Clean Sections', 'Corporate Style', 'Professional Fonts', 'Structured']
  },
  {
    id: 'creative',
    name: 'Creative Sidebar',
    description: 'Eye-catching sidebar layout ideal for creative and design roles',
    category: 'Creative',
    color: 'from-purple-600 to-indigo-700',
    preview: 'Creative design with dark sidebar, modern fonts, and visual hierarchy',
    features: ['Sidebar Layout', 'Dark Theme', 'Modern Typography', 'Visual Impact']
  },
  {
    id: 'minimal',
    name: 'Minimal Elegant',
    description: 'Simple and sophisticated with emphasis on content clarity',
    category: 'Minimal',
    color: 'from-emerald-500 to-teal-600',
    preview: 'Minimal design with clean lines, subtle colors, and content-focused layout',
    features: ['Clean Lines', 'Subtle Colors', 'Content Focus', 'Elegant']
  },
  {
    id: 'bold-blue',
    name: 'Bold Blue Impact',
    description: 'Vibrant blue header with bold typography and modern pill-shaped skill tags',
    category: 'Bold',
    color: 'from-blue-700 via-blue-600 to-cyan-500',
    preview: 'Bold blue template with uppercase headers, blue accent pills, and strong visual impact',
    features: ['Blue Gradient', 'Bold Typography', 'Pill Tags', 'Modern']
  },
  {
    id: 'coral-pink',
    name: 'Coral Warmth',
    description: 'Warm coral and pink gradient with vertical accent bars',
    category: 'Creative',
    color: 'from-pink-500 via-rose-400 to-orange-400',
    preview: 'Warm and modern with pink-orange gradients and vertical sidebar accents',
    features: ['Coral Pink', 'Vertical Bars', 'Grid Skills', 'Warm Colors']
  },
  {
    id: 'navy-gold',
    name: 'Navy Gold Elite',
    description: 'Professional navy with elegant gold accents and serif typography',
    category: 'Executive',
    color: 'from-slate-800 via-slate-700 to-slate-800',
    preview: 'Navy background with gold top stripe, diamond bullets, and elegant serif fonts',
    features: ['Navy Blue', 'Gold Accents', 'Serif Fonts', 'Diamond Bullets']
  },
  {
    id: 'lime-green',
    name: 'Lime Fresh Energy',
    description: 'Energetic lime green with bold fonts and rounded elements',
    category: 'Bold',
    color: 'from-lime-500 via-green-400 to-emerald-500',
    preview: 'Fresh lime green with extra bold fonts, rounded boxes, and high energy design',
    features: ['Lime Green', 'Bold Fonts', 'Rounded Elements', 'Energetic']
  },
  {
    id: 'crimson-red',
    name: 'Crimson Power',
    description: 'Powerful crimson red with strong typography and timeline design',
    category: 'Bold',
    color: 'from-red-700 via-red-600 to-rose-600',
    preview: 'Bold red template with timeline, strong typography, and powerful visual presence',
    features: ['Crimson Red', 'Timeline Design', 'Bold Headers', 'Powerful']
  }
]

// Sample resume data for previews
const sampleData = {
  personalInfo: {
    fullName: 'JOHN SMITH',
    email: 'john.smith@email.com',
    phone: '+1 (555) 123-4567',
    address: 'New York, NY',
    linkedin: 'linkedin.com/in/johnsmith',
    website: 'johnsmith.dev',
    github: 'github.com/johnsmith',
    photo: ''
  },
  summary: 'Results-driven Software Engineer with 5+ years of experience in full-stack development and cloud architecture.',
  experience: [
    {
      id: '1',
      jobTitle: 'Senior Software Engineer',
      company: 'Tech Innovations Inc.',
      location: 'New York, NY',
      startDate: 'Jan 2022',
      endDate: '',
      description: '• Led development of microservices architecture\n• Mentored team of 4 junior developers'
    }
  ],
  education: [
    {
      id: '1',
      degree: 'Bachelor of Science in Computer Science',
      institution: 'MIT',
      location: 'Cambridge, MA',
      graduationDate: 'May 2020',
      gpa: '3.8'
    }
  ],
  skills: [
    {
      category: 'Programming',
      skills: ['JavaScript', 'TypeScript', 'Python']
    },
    {
      category: 'Frontend',
      skills: ['React', 'Vue.js', 'Tailwind CSS']
    }
  ],
  projects: [
    {
      id: '1',
      name: 'E-Commerce Platform',
      description: 'Full-stack application with real-time inventory',
      technologies: ['React', 'Node.js', 'PostgreSQL']
    }
  ],
  certifications: [
    {
      id: '1',
      name: 'AWS Certified Solutions Architect',
      issuer: 'Amazon Web Services',
      date: 'March 2023'
    }
  ]
}

export default function TemplateGallery({ selectedTemplate, onSelect, onClose }: TemplateGalleryProps) {
  const [hoveredTemplate, setHoveredTemplate] = useState<string | null>(null)
  const [currentIndex, setCurrentIndex] = useState(0)

  // Auto-rotate templates every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % templates.length)
    }, 3000)
    return () => clearInterval(interval)
  }, [])

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % templates.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + templates.length) % templates.length)
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-white rounded-2xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Sparkles className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Choose Your Template</h2>
                <p className="text-blue-100 text-sm mt-1">Select a professional design that matches your style</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 rounded-full p-2 transition-all"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Carousel Navigation */}
        <div className="p-8 overflow-y-auto max-h-[calc(90vh-120px)]">
          {/* Navigation Arrows */}
          <div className="flex items-center justify-between mb-6">
            <button
              onClick={prevSlide}
              className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition-all shadow-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="text-center">
              <p className="text-gray-600 text-sm">Template {currentIndex + 1} of {templates.length}</p>
            </div>
            <button
              onClick={nextSlide}
              className="bg-blue-600 text-white p-3 rounded-full hover:bg-blue-700 transition-all shadow-lg"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>

          {/* Carousel Container */}
          <div className="relative overflow-hidden">
            <div
              className="flex transition-transform duration-500 ease-in-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}
            >
              {templates.map((template) => {
              const isSelected = selectedTemplate === template.id
              const isHovered = hoveredTemplate === template.id

              return (
                <div
                  key={template.id}
                  onClick={() => {
                    onSelect(template.id)
                    setTimeout(() => onClose(), 300)
                  }}
                  onMouseEnter={() => setHoveredTemplate(template.id)}
                  onMouseLeave={() => setHoveredTemplate(null)}
                  className={`flex-shrink-0 w-full px-4 group relative bg-white rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${
                    isSelected
                      ? 'border-4 border-blue-600 shadow-xl shadow-blue-300 ring-2 ring-blue-200'
                      : 'border-2 border-gray-200 hover:border-blue-400 hover:shadow-blue-100'
                  } ${
                    isHovered ? 'shadow-2xl scale-[1.01]' : 'shadow-md hover:shadow-lg'
                  }`}
                >
                  {/* Selected Badge */}
                  {isSelected && (
                    <div className="absolute top-4 right-4 z-10 bg-blue-500 text-white rounded-full p-2 shadow-lg">
                      <Check className="w-5 h-5" />
                    </div>
                  )}

                  {/* Template Preview - Real Resume Preview */}
                  <div className="h-96 relative overflow-hidden bg-gray-100 flex items-center justify-center">
                    <div className="transform" style={{ transform: 'scale(0.36)', transformOrigin: 'center' }}>
                      <ResumePreview
                        data={sampleData}
                        template={template.id}
                        language="en"
                      />
                    </div>

                    {/* Category Badge */}
                    <div className="absolute top-4 left-4 bg-white bg-opacity-95 text-gray-800 text-xs font-semibold px-3 py-1 rounded-full z-10 shadow-lg backdrop-blur-sm">
                      {template.category}
                    </div>
                  </div>

                  {/* Template Info */}
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{template.name}</h3>
                    <p className="text-gray-600 text-sm mb-4">{template.description}</p>

                    {/* Features */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {template.features.map((feature, idx) => (
                        <span
                          key={idx}
                          className="text-xs bg-gray-100 text-gray-700 px-3 py-1 rounded-full"
                        >
                          {feature}
                        </span>
                      ))}
                    </div>

                    {/* Select Button - Shows on hover */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation() // Prevent double-click
                        onSelect(template.id)
                        setTimeout(() => onClose(), 300)
                      }}
                      className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                        isSelected
                          ? 'bg-blue-500 text-white'
                          : isHovered
                          ? 'bg-blue-500 text-white transform scale-105'
                          : 'bg-gray-100 text-gray-700 opacity-0 group-hover:opacity-100'
                      }`}
                    >
                      {isSelected ? (
                        <span className="flex items-center justify-center space-x-2">
                          <Check className="w-5 h-5" />
                          <span>Currently Selected</span>
                        </span>
                      ) : (
                        'Select Template'
                      )}
                    </button>
                  </div>

                  {/* Hover Overlay */}
                  {isHovered && !isSelected && (
                    <div className="absolute inset-0 bg-gradient-to-t from-blue-500 to-transparent opacity-5 pointer-events-none"></div>
                  )}
                </div>
              )
            })}
            </div>

            {/* Dot Indicators */}
            <div className="flex justify-center space-x-2 mt-6">
              {templates.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-3 h-3 rounded-full transition-all ${
                    index === currentIndex
                      ? 'bg-blue-600 w-8'
                      : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        .animate-fadeIn {
          animation: fadeIn 0.2s ease-out;
        }

        .bg-grid-white {
          background-image: url("data:image/svg+xml,%3Csvg width='20' height='20' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h20v20H0z' fill='none'/%3E%3Cpath d='M0 0h1v1H0zM19 19h1v1h-1z' fill='white' fill-opacity='0.3'/%3E%3C/svg%3E");
        }
      `}</style>
    </div>
  )
}
